import { useState, useEffect } from 'react';
import IconifyIcon from '../../../components/IconifyIcon';
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import apiService from '../../../services/api.service';
import { getOrderTotals } from '../../../utils/orderTotals';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const ordersPerPage = 5;

  useEffect(() => {
    fetchOrders();
  }, [currentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/orders?page=${currentPage}&per_page=${ordersPerPage}&sort=created_at&order=desc`);
      if (response.success) {
        const ordersData = response.data.data || response.data;
        // Limiter à 5 commandes max
        setOrders(Array.isArray(ordersData) ? ordersData.slice(0, ordersPerPage) : []);
        setTotalOrders(response.data.total || ordersData.length);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'shipped':
      case 'processing':
        return 'warning';
      case 'cancelled':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'Livré';
      case 'completed': return 'Terminé';
      case 'shipped': return 'Expédié';
      case 'processing': return 'En cours';
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 3;
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <span role="button" className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </span>
        </li>
      );
    }
    return items;
  };

  return (
    <Col className="mt-3">
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between border-bottom">
          <CardTitle as={'h4'} className="mb-0">Commandes Récentes</CardTitle>
          <Link to="/admin/orders">
            <Button variant="soft-primary" size="sm">
              Voir Tout
            </Button>
          </Link>
        </CardHeader>
        <div className="table-responsive table-centered" style={{ padding: '1.25rem' }}>
          <table className="table mb-0">
            <thead className="bg-light bg-opacity-50">
              <tr>
                <th className="ps-3 fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>N° Commande</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Date</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Client</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Téléphone</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Ville</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Total</th>
                <th className="fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Chargement...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    Aucune commande trouvée
                  </td>
                </tr>
              ) : (
                orders.map((order, idx) => (
                  <tr key={order.id || idx}>
                    <td className="ps-3">
                      <Link to={`/admin/orders/${order.id}`} className="text-primary text-decoration-none fw-semibold">
                        {order.order_number || `#${order.id}`}
                      </Link>
                    </td>
                    <td className="text-dark">{formatDate(order.created_at)}</td>
                    <td>
                      <span className="text-dark">{order.first_name} {order.last_name}</span>
                    </td>
                    <td className="text-dark">{order.phone || '-'}</td>
                    <td className="text-dark">{order.city || '-'}</td>
                    <td className="text-dark fw-semibold">{`${getOrderTotals(order).grandTotal.toFixed(2)} DH`}</td>
                    <td>
                      <IconifyIcon
                        icon="bxs:circle"
                        className={`text-${getStatusColor(order.status)} me-1`}
                        style={{ fontSize: '0.5rem' }}
                      />
                      <span className="text-dark">{getStatusLabel(order.status)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <CardFooter className="border-top">
          <Row className="g-3 align-items-center">
            <div className="col-sm">
              <div className="text-muted">
                Affichage
                <span className="fw-semibold text-dark ms-1 me-1">{orders.length}</span>
                sur
                <span className="fw-semibold text-dark ms-1 me-1">{totalOrders}</span>
                commandes
              </div>
            </div>
            <Col sm={'auto'}>
              <ul className="pagination m-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <span role="button" className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                    <IconifyIcon icon="bx:left-arrow-alt" />
                  </span>
                </li>
                {renderPaginationItems()}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <span role="button" className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                    <IconifyIcon icon="bx:right-arrow-alt" />
                  </span>
                </li>
              </ul>
            </Col>
          </Row>
        </CardFooter>
      </Card>
    </Col>
  );
};

export default Orders;
